import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Shield, Users, AlertCircle, Scale, CheckCircle } from 'lucide-react';
import { useTermsOfService } from '../../hooks/useTermsOfService';
import { MarketingPage } from '../../components/home/MarketingPageChrome';

const iconMap = {
  FileText,
  Shield,
  Users,
  AlertCircle,
  Scale,
  CheckCircle,
};

const LEGAL_NAV = [
  { to: '/terms', label: 'Terms of Service', active: true },
  { to: '/privacy', label: 'Privacy Policy', active: false },
];

const TermsOfService = () => {
  const { data: termsResponse, isLoading, error } = useTermsOfService();
  const termsData = termsResponse?.data;

  const sections = useMemo(() => {
    if (!termsData?.sections) return [];
    return termsData.sections.map((section) => ({
      ...section,
      icon: iconMap[section.icon] || FileText,
    }));
  }, [termsData]);

  const settings = useMemo(() => termsData?.settings || {}, [termsData]);
  const lastUpdated = settings?.last_updated || 'November 2, 2025';

  return (
    <MarketingPage>
      <div className="border-b border-m-border-light bg-white">
        <nav className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
          <ul className="flex flex-wrap gap-x-6 gap-y-3">
            {LEGAL_NAV.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className={`text-sm font-medium transition-colors ${
                    item.active
                      ? 'border-b-2 border-m-ink pb-1 text-m-ink'
                      : 'text-m-ink-muted hover:text-m-ink'
                  }`}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>

      {!isLoading && sections.length > 0 ? (
        <div className="sticky top-16 z-10 border-b border-m-border-light bg-white/95 backdrop-blur-sm">
          <div className="mx-auto max-w-4xl px-4 sm:px-6">
            <div className="flex gap-1 overflow-x-auto py-3 scrollbar-hide">
              {sections.map((section, index) => (
                <a
                  key={section.id || index}
                  href={`#section-${index}`}
                  className="shrink-0 px-3 py-1.5 text-sm font-medium text-m-ink-muted transition-colors hover:text-m-ink"
                >
                  {section.title}
                </a>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      <article className="mx-auto max-w-4xl px-4 py-12 sm:px-6 sm:py-16">
        <header className="mb-10">
          <h1
            className="marketing-hero-title mb-3"
            style={{ color: '#0a0a0a' }}
          >
            Terms of Service
          </h1>
          <p className="text-sm" style={{ color: '#6b6b6b' }}>
            Last updated: {lastUpdated}
          </p>
        </header>

        {settings?.introduction ? (
          <p
            className="mb-10 whitespace-pre-line text-[1.05rem] leading-relaxed"
            style={{ color: '#0a0a0a' }}
          >
            {settings.introduction}
          </p>
        ) : null}

        {isLoading ? (
          <div className="animate-pulse space-y-4 py-8">
            <div className="h-6 w-3/4 rounded bg-m-canvas-light" />
            <div className="h-4 rounded bg-m-canvas-light" />
            <div className="h-4 w-5/6 rounded bg-m-canvas-light" />
          </div>
        ) : null}

        {error && !isLoading ? (
          <p className="py-8 text-sm" style={{ color: '#6b6b6b' }}>
            Failed to load terms of service. Please try again later.
          </p>
        ) : null}

        {!isLoading &&
          sections.map((section, index) => {
            const IconComponent = section.icon || FileText;
            return (
              <section
                key={section.id || index}
                id={`section-${index}`}
                className="scroll-mt-28 border-b border-m-border-light py-10 first:border-t first:border-m-border-light"
              >
                <div className="mb-4 flex items-center gap-3" style={{ color: '#0a0a0a' }}>
                  <IconComponent className="h-5 w-5" strokeWidth={1.5} />
                  <h2 className="text-xl font-bold tracking-tight sm:text-2xl">
                    {section.title}
                  </h2>
                </div>
                <div className="space-y-4">
                  {(section.content || []).map((paragraph, pIndex) => (
                    <p
                      key={pIndex}
                      className="text-[15px] leading-relaxed"
                      style={{ color: '#0a0a0a' }}
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>
              </section>
            );
          })}

        {!isLoading && sections.length === 0 && !error ? (
          <p className="py-8 text-sm" style={{ color: '#6b6b6b' }}>
            No terms of service sections available at this time.
          </p>
        ) : null}

        <footer className="mt-12 space-y-4 text-sm leading-relaxed" style={{ color: '#6b6b6b' }}>
          <p>
            By using VendaList, you acknowledge that you have read, understood, and agree to be
            bound by these Terms of Service. If you do not agree, please do not use our services.
          </p>
          <p>
            Questions?{' '}
            <Link to="/contact" className="underline underline-offset-2 hover:text-m-ink">
              Contact support
            </Link>
            {' · '}
            <Link to="/privacy" className="underline underline-offset-2 hover:text-m-ink">
              Privacy Policy
            </Link>
          </p>
        </footer>
      </article>
    </MarketingPage>
  );
};

export default TermsOfService;
